import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, ArrowRight, ExternalLink, Edit2 } from 'lucide-react';
import { FeaturePopup } from '../../models/FeaturePopup';

interface FeatureGraphicPopupModalProps {
  popup: FeaturePopup | null;
  isOpen: boolean;
  onDismiss: () => void;
  onSnooze: () => void;
  onAction: () => void;
  onEdit?: () => void;
  isPreview?: boolean;
}

export const FeatureGraphicPopupModal: React.FC<FeatureGraphicPopupModalProps> = ({
  popup,
  isOpen,
  onDismiss,
  onSnooze,
  onAction,
  onEdit,
  isPreview = false,
}) => {
  if (!isOpen || !popup) return null;

  const actionText = popup.actionButtonText?.trim() || 'Get More Information';
  const hasAction = popup.actionType !== 'none';

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9995] flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={popup.title || 'Special Feature'}
        onClick={onDismiss}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-lg bg-[#0e121d] border border-white/10 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col my-auto max-h-[92vh]"
          onClick={(e) => e.stopPropagation()}
          style={{ fontFamily: 'var(--font-base)' }}
        >
          {isPreview && (
            <div className="bg-amber-500 text-black text-[11px] font-black uppercase tracking-wider py-1.5 px-3 z-30 flex items-center justify-between gap-2 shadow-md">
              <span className="truncate">Admin Preview &mdash; Dismissal tracking disabled</span>
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="flex items-center gap-1 px-2.5 py-0.5 bg-black hover:bg-neutral-800 text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap active:scale-95"
                  title="Edit this pop-up"
                >
                  <Edit2 size={11} />
                  <span>Edit Pop-up</span>
                </button>
              )}
            </div>
          )}

          {/* Graphic Container (In the middle) */}
          <div className="relative w-full overflow-hidden bg-black/60 flex items-center justify-center min-h-[220px] max-h-[65vh]">
            {/* X Button at the top as an overlay on the graphic */}
            <button
              onClick={onDismiss}
              aria-label="Close"
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/65 hover:bg-black/90 text-white flex items-center justify-center backdrop-blur-md shadow-lg border border-white/20 transition-all hover:scale-110 active:scale-95 cursor-pointer group"
            >
              <X size={18} className="text-white/90 group-hover:text-white transition-colors" />
            </button>

            {popup.imageUrl ? (
              <img
                src={popup.imageUrl}
                alt={popup.title || 'Feature announcement graphic'}
                className="w-full h-auto max-h-[65vh] object-contain sm:object-cover select-none"
                loading="eager"
              />
            ) : (
              <div className="w-full py-16 flex flex-col items-center justify-center text-slate-400 p-6 text-center">
                <span className="text-4xl mb-2">🃏</span>
                <p className="text-sm font-semibold text-white/80">{popup.title}</p>
                <p className="text-xs text-white/40 mt-1">No feature graphic uploaded yet.</p>
              </div>
            )}
          </div>

          {/* Buttons at the bottom */}
          <div className="p-3.5 sm:p-5 bg-gradient-to-b from-[#111625] to-[#0a0d16] flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-3 border-t border-white/10">
            {/* Remind Me Later button */}
            <button
              onClick={onSnooze}
              type="button"
              className="w-full sm:w-auto px-4 py-2.5 text-xs sm:text-sm font-bold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 active:bg-white/15 rounded-xl transition-all text-center flex items-center justify-center gap-1.5 cursor-pointer order-2 sm:order-1"
            >
              <Clock size={15} className="text-slate-400" />
              Remind Me Later
            </button>

            {/* Get More Information / Action button */}
            {hasAction ? (
              <button
                onClick={onAction}
                type="button"
                className="w-full sm:w-auto flex-1 sm:flex-initial px-5 py-2.5 bg-gradient-to-r from-[#c72f38] to-[#df333e] hover:from-[#b0222b] hover:to-[#c72f38] text-white text-xs sm:text-sm font-black rounded-xl shadow-lg shadow-red-950/40 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer order-1 sm:order-2"
              >
                <span>{actionText}</span>
                {popup.actionType === 'url' && popup.actionUrl?.startsWith('http') ? (
                  <ExternalLink size={15} />
                ) : (
                  <ArrowRight size={15} />
                )}
              </button>
            ) : (
              <button
                onClick={onDismiss}
                type="button"
                className="w-full sm:w-auto flex-1 sm:flex-initial px-5 py-2.5 bg-gradient-to-r from-[#c72f38] to-[#df333e] text-white text-xs sm:text-sm font-black rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center order-1 sm:order-2"
              >
                Got It
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
